import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { isExisty } from 'utils/helpers.util';
import calculateHeaderHeight, {
  CHECKOUT_HEADER_HEIGHT,
} from 'common/Header/constants/headerHeight.constant';
import { NEW_CHECKOUT_PAGE } from 'config/Urls.config';
import { isCurrentPage } from 'utils/url.util';
import {
  IOrderSummaryState,
  defaultPlasticBagPackingInfoForNoBag,
} from './redux/orderSummary.reducer';

import useOrderSummary from './hooks/useOrderSummary.hook';
import useStickyTotalHeight from './hooks/useStickyTotalHeight.hook';

import OrderSummaryLoader from './components/OrderSummaryLoader';
import OrderSummaryLayout from './components/OrderSummaryLayout';
import COLORS from 'theme/colors';
import FONTFAMILY from 'theme/fontFamily';
import { useSelector } from 'react-redux';
import { IAppState } from '@mafc/common';
import { PlasticBagPackingInfo } from 'network/response/ICart';
import useNewC4Journey from 'hooks/useNewC4Journey';

interface Props {
  children: (
    arg1: IOrderSummaryState,
    arg2: any,
    arg3: PlasticBagPackingInfo | undefined
  ) => React.ReactNode;
  isLoading?: boolean;
  ShareCart?: React.ReactNode;
  Promo?: React.ReactNode;
  PaymentMethods?: React.ReactNode;
  bg?: string;
  paddingStyle?: any;
  hideBinLoading?: boolean;
}

const OrderSummary: React.FC<Props> = ({
  isLoading = false,
  bg = COLORS.WHITE,
  hideBinLoading,
  children,
  ...rest
}) => {
  const [stickyTotalEl] = useStickyTotalHeight();
  const orderSummaryState = useOrderSummary();
  const isNewC4Journey = useNewC4Journey();
  let plasticBagPackingInfo;
  const {
    cart: {
      cartData: { packingCharges, sustainabilityConsent } = {
        packingCharges: {} as PlasticBagPackingInfo,
        sustainabilityConsent: false,
      },
    },
  } = useSelector((state: IAppState) => state);

  plasticBagPackingInfo = sustainabilityConsent
    ? defaultPlasticBagPackingInfoForNoBag
    : packingCharges;

  // Consuming currenyName for other languages as we have given hardcoded curreny name in defaultPlasticBagPackingInfoForNoBag
  if (sustainabilityConsent && plasticBagPackingInfo) {
    plasticBagPackingInfo.totalCharges.currencyName =
      orderSummaryState.totalPriceWithTax.currencyName;
  }

  const { subTotal, totalDiscounts } = orderSummaryState;

  const { pathname } = useLocation();
  const isCheckoutPage = isCurrentPage(NEW_CHECKOUT_PAGE, pathname);

  const hasDataToRender = isExisty(subTotal) || isExisty(totalDiscounts);

  const { t } = useTranslation();

  if (isLoading) {
    return <OrderSummaryLoader />;
  }
  localStorage.setItem('isPurchaseProcessStarted', 'true');
  // TODO:Add text alignment,color, font-weight, font-size in trolly text component

  const adjustTopForNewCart = isNewC4Journey && !isCheckoutPage ? 18 : 0;
  return hasDataToRender ? (
    <OrderSummaryLayout
      isCheckoutPage={isCheckoutPage}
      bg={bg}
      hideBinLoading={hideBinLoading}
      title={t('orderSummary.heading')}
      pb={[0, null, null, 0]}
      sx={{
        alignSelf: 'flex-start',
        position: [null, null, null, 'sticky'],
        fontFamily: FONTFAMILY.PRIMARY,
        top: [
          null,
          null,
          null,
          ((isCheckoutPage ? CHECKOUT_HEADER_HEIGHT : calculateHeaderHeight(0))[3] as number) +
            70 +
            adjustTopForNewCart,
        ],
      }}
      {...rest}
    >
      {children(orderSummaryState, stickyTotalEl, plasticBagPackingInfo)}
    </OrderSummaryLayout>
  ) : null;
};

export default OrderSummary;
